import {
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from "@mui/material";
import axios from "../../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { useNavigate } from "react-router";
import {
    LOCAL_DATE_CONVERSION,
    dateDifference,
} from "../../../../utilities/helpers/formatUnix";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
    DELETE_YMAL_FAILED,
    DELETE_YMAL_SUCCESS,
    DRAFT_STATUS,
} from "../../LandingPage/constants";
import DeleteModal from "../../../UI/atoms/Popup/DeleteModal";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../../utils/permissions";

dayjs.extend(utc);
dayjs.extend(timezone);

const YmalTable = forwardRef(
    ({ status, showActionButton, refreshDraft }: any, ref: any) => {
        // hooks
        const navigate = useNavigate();
        const { hasPermission } = usePermissions();
        const [loader, setLoader] = useState(false);
        const { enqueueSnackbar } = useSnackbar();
        const [selectYmal, setSelectedYmal] = useState<any>(null);
        const [mappingList, setMappingList] = useState<any>([]);
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);
        const [totalCount, setTotalCount] = useState(0);
        const [skipKey, setSkipKey] = useState("");
        const [skipKeyQueue, setskipKeyQueue] = useState<any>([]);
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

        const closeDeleteModal = () => {
            setIsDeleteModalOpen(false);
            setSelectedYmal(null);
        };

        useImperativeHandle(
            ref,
            () => ({
                getMappingList,
            }),
            []
        );

        useEffect(() => {
            getMappingList();
        }, []);

        const resetTableMeta = () => {
            setskipKeyQueue([]);
            setPage(0);
        };

        const getMappingList = async () => {
            const pages = await getYmalMappingList(status, rowsPerPage);
            setTotalCount(pages.totalCount);
            setMappingList([...pages.list]);
        };

        const getYmalMappingList = async (
            status: any,
            limit: number | string = rowsPerPage || 0,
            skip: string = skipKey || "",
            skipQueue: any = skipKeyQueue
        ) => {
            const pages = await getYmal(status, limit, skip);
            const list = pages?.list;
            list.length &&
                pushKey(
                    `${list[list.length - 1]?.id}__${
                        status == DRAFT_STATUS
                            ? list[list.length - 1]?.updateAt
                            : list[list.length - 1]?.publishDateTime
                    }`,
                    skipQueue
                );
            return pages;
        };

        const getYmal = async (
            status: string,
            limit: number | string,
            skip: string = ""
        ) => {
            try {
                setLoader(true);
                const saveVersion = await axios.get(
                    `${process.env.BASE_URL}api/v1/cms/ymal/filter?status=${status}&skip=${skip}&limit=${limit}`
                );
                return saveVersion.data?.data;
            } catch (error: any) {
                console.log(
                    "Error in getting master information",
                    error.response
                );
                enqueueSnackbar(error.response?.data?.message, {
                    variant: "warning",
                });
            } finally {
                setLoader(false);
            }
        };

        const openDeleteModal = (row: any) => {
            setSelectedYmal(row);
            setIsDeleteModalOpen(true);
        };

        const tableRowRender = () => {
            return (
                <TableRow>
                    <TableCell sx={{ width: 200 }}>
                        <h3>Content ID</h3>
                    </TableCell>
                    <TableCell sx={{ width: 150 }}>
                        <h3>Content Type</h3>
                    </TableCell>
                    <TableCell sx={{ width: 150 }}>
                        <h3>Status</h3>
                    </TableCell>
                    <TableCell sx={{ width: 200 }}>
                        <h3>Playlist ID</h3>
                    </TableCell>
                    <TableCell sx={{ width: 200 }}>
                        <h3>Publish Date</h3>
                    </TableCell>
                    {hasPermission(PERMISSIONS.curation, ACTION.EDIT) && (
                        <TableCell align="center" sx={{ width: 150 }}>
                            <h3>Actions</h3>
                        </TableCell>
                    )}
                </TableRow>
            );
        };

        const handleChangePage = async (event: unknown, newPage: number) => {
            let skipKeyValue = "";
            let queue;
            if (newPage < page) {
                queue = popTwice();
                setskipKeyQueue([...queue]);
                skipKeyValue = queue[queue.length - 1] || "";
                console.log(skipKeyQueue);
            } else {
                skipKeyValue = skipKeyQueue[skipKeyQueue.length - 1];
            }
            console.log("newPage", newPage);
            setPage(newPage);
            const pages = await getYmalMappingList(
                status,
                rowsPerPage,
                skipKeyValue,
                queue
            );
            setMappingList([...pages.list]);
            setTotalCount(pages.totalCount);
        };

        const handleChangeRowsPerPage = async (
            event: React.ChangeEvent<HTMLInputElement>
        ) => {
            resetTableMeta();
            setRowsPerPage(parseInt(event.target.value, 10));
            const pages = await getYmalMappingList(
                status,
                event.target.value,
                undefined,
                []
            );
            setMappingList([...pages.list]);
            setTotalCount(pages.totalCount);
        };

        const pushKey = (key: any, queue: any = skipKeyQueue) => {
            const skipValue = [...queue, key];
            setskipKeyQueue(skipValue);
        };

        const popKey = (queue: any[] = skipKeyQueue) => {
            const skipValue = [...queue];
            const popValue = skipValue.pop();
            setskipKeyQueue(skipValue);
            return skipValue;
        };

        const popTwice = () => {
            const queue1 = popKey();
            const queue = popKey(queue1);
            return queue;
        };

        const showDateBasedOnStatus = (row: any) => {
            return (
                <>
                    <TableCell>
                        {LOCAL_DATE_CONVERSION(row?.publishDateTime)}{" "}
                        {dayjs(row.publishDateTime).format("hh:mm A")}
                    </TableCell>
                </>
            );
        };

        const deleteMapping = async () => {
            try {
                setLoader(true);
                const deleteRail = await axios.delete(
                    `${process.env.BASE_URL}api/v1/cms/ymal/${selectYmal.id}`
                );
                if (deleteRail?.data?.success) {
                    enqueueSnackbar(DELETE_YMAL_SUCCESS, {
                        variant: "success",
                    });
                    getMappingList();
                    closeDeleteModal();
                } else {
                    enqueueSnackbar(DELETE_YMAL_FAILED, { variant: "error" });
                }
            } catch (error: any) {
                enqueueSnackbar(DELETE_YMAL_FAILED, { variant: "error" });
                console.log("Error in getting master information", error);
            } finally {
                setLoader(false);
                setIsDeleteModalOpen(false);
            }
        };

        return (
            <div>
                <div>
                    <h4 className="text-capitalize">
                        {status}{" "}
                        {loader && (
                            <CircularProgress className="ml-1" size={24} />
                        )}
                    </h4>
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>{tableRowRender()}</TableHead>
                            <TableBody>
                                {mappingList &&
                                    mappingList.map(
                                        (row: any, index: number) => (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    "&:last-child td, &:last-child th":
                                                        { border: 0 },
                                                }}
                                            >
                                                <TableCell>
                                                    <h4> {row.assetTitle}</h4>
                                                    {row?.assetId || ""}
                                                </TableCell>
                                                <TableCell className="text-capitalize">
                                                    {row?.contentType || ""}
                                                </TableCell>
                                                <TableCell className="text-capitalize">
                                                    {row?.publishStatus || "-"}
                                                </TableCell>

                                                <TableCell>
                                                    <h4> {row.playlistId}</h4>
                                                    {row?.playlistTitle || ""}
                                                </TableCell>

                                                {showDateBasedOnStatus(row)}

                                                {hasPermission(
                                                    PERMISSIONS.curation,
                                                    ACTION.EDIT
                                                ) && (
                                                    <TableCell
                                                        sx={{
                                                            paddingRight: 0,
                                                            paddingLeft: 0,
                                                        }}
                                                        align="center"
                                                    >
                                                        <Tooltip
                                                            className="cursor-pointer"
                                                            title="Delete"
                                                        >
                                                            <DeleteIcon
                                                                className="action-icons"
                                                                onClick={() =>
                                                                    openDeleteModal(
                                                                        row
                                                                    )
                                                                }
                                                            />
                                                        </Tooltip>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        )
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </div>

                <DeleteModal
                    title="Delete Mapping?"
                    isOpen={isDeleteModalOpen}
                    handleClose={closeDeleteModal}
                    handleConfirm={deleteMapping}
                >
                    <p>Are you sure you want to delete this mapping?</p>
                </DeleteModal>
            </div>
        );
    }
);

export default YmalTable;
